import { writable, derived, get } from 'svelte/store';
import { fetchCategories, fetchProducts } from '../services/api';

// Estado inicial 100% em branco para novos estabelecimentos
const INITIAL_CATEGORIES = [];
const INITIAL_PRODUCTS = [];

// Carrega do LocalStorage apenas se houver dados customizados do usuário, caso contrário inicia vazio
function loadInitialState() {
    if (typeof localStorage !== 'undefined') {
        // Limpa chaves legadas com cardápio demo para não poluir novos tenants
        try {
            const saved = localStorage.getItem('matafome_menu_state_v2');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed && Array.isArray(parsed.categories) && parsed.categories.length > 0) {
                    // Se o primeiro item for o mockup antigo 'cat-1', limpa do storage
                    if (parsed.categories[0].id === 'cat-1') {
                        localStorage.removeItem('matafome_menu_state_v2');
                        localStorage.removeItem('pediria_menu_state_v2');
                        localStorage.removeItem('pediria_menu_state_v1');
                    } else {
                        return {
                            categories: parsed.categories,
                            products: parsed.products || []
                        };
                    }
                }
            }
        } catch (_) {}
    }
    return {
        categories: [],
        products: []
    };
}

const initialState = loadInitialState();
const menuState = writable(initialState);

// Persistência automática
if (typeof localStorage !== 'undefined') {
    menuState.subscribe((val) => {
        if (val && Array.isArray(val.categories) && val.categories.length > 0) {
            localStorage.setItem('matafome_menu_state_v2', JSON.stringify(val));
        }
    });
}

// Stores derivadas públicas
export const categories = derived(menuState, ($state) =>
    [...$state.categories].sort((a, b) => a.order - b.order)
);

export const products = derived(menuState, ($state) => $state.products);

// Ações e Lógica de Negócio (CRUD Completo)
export const menuStore = {
    subscribe: menuState.subscribe,

    // Sincroniza em tempo real com o banco de dados PostgreSQL
    async syncFromApi() {
        try {
            const [apiCategories, apiProducts] = await Promise.all([
                fetchCategories(),
                fetchProducts()
            ]);

            const mappedCategories = (apiCategories && Array.isArray(apiCategories))
                ? apiCategories.map((c, idx) => ({
                    id: c.id,
                    name: c.name,
                    icon: c.icon || '🍽️',
                    description: c.description || '',
                    image: c.image || '',
                    active: Boolean(c.active !== 0 && c.active !== false),
                    order: c.display_order || idx + 1
                }))
                : [];

            const mappedProducts = (apiProducts && Array.isArray(apiProducts))
                ? apiProducts.map(p => ({
                    id: p.id,
                    categoryId: p.category_id || p.categoryId,
                    name: p.name,
                    description: p.description || '',
                    price: parseFloat(p.price) || 0,
                    promoPrice: parseFloat(p.promo_price || p.promoPrice) || 0,
                    image: p.image || '',
                    active: Boolean(p.active !== 0 && p.active !== false),
                    modifierGroups: p.modifier_groups || p.modifierGroups || [],
                    track_stock: Boolean(p.track_stock),
                    stock_quantity: p.stock_quantity !== undefined && p.stock_quantity !== null ? parseInt(p.stock_quantity, 10) : 0,
                    auto_paused_by_stock: Boolean(p.auto_paused_by_stock)
                }))
                : [];

            menuState.set({
                categories: mappedCategories,
                products: mappedProducts
            });
            return true;
        } catch (err) {
            console.warn('[menuStore] Erro ao sincronizar cardápio do backend:', err.message);
        }
        return false;
    },

    // ================= CATEGORIAS =================
    addCategory(cat) {
        let newCat;
        menuState.update((state) => {
            const nextOrder = state.categories.length + 1;
            newCat = {
                id: cat.id || `cat-${Date.now()}`,
                name: (cat.name || '').trim(),
                icon: (cat.icon || '🍽️').trim() || '🍽️',
                description: (cat.description || '').trim(),
                image: cat.image || '',
                active: cat.active !== undefined ? Boolean(cat.active) : true,
                order: cat.order || cat.display_order || nextOrder
            };
            return {
                ...state,
                categories: [...state.categories, newCat]
            };
        });
        return newCat;
    },

    updateCategory(id, updates) {
        menuState.update((state) => ({
            ...state,
            categories: state.categories.map((c) => (c.id === id ? { ...c, ...updates } : c))
        }));
    },

    deleteCategory(id) {
        menuState.update((state) => ({
            ...state,
            categories: state.categories.filter((c) => c.id !== id),
            // Remove ou desvincula produtos da categoria deletada
            products: state.products.filter((p) => p.categoryId !== id)
        }));
    },

    toggleCategoryStatus(id) {
        menuState.update((state) => ({
            ...state,
            categories: state.categories.map((c) =>
                c.id === id ? { ...c, active: !c.active } : c
            )
        }));
    },

    moveCategory(id, direction) {
        menuState.update((state) => {
            const sorted = [...state.categories].sort((a, b) => a.order - b.order);
            const index = sorted.findIndex((c) => c.id === id);
            if (index === -1) return state;

            const targetIndex = direction === "up" ? index - 1 : index + 1;
            if (targetIndex < 0 || targetIndex >= sorted.length) return state;

            // Troca a ordem
            const tempOrder = sorted[index].order;
            sorted[index].order = sorted[targetIndex].order;
            sorted[targetIndex].order = tempOrder;

            return {
                ...state,
                categories: sorted
            };
        });
    },

    // ================= PRODUTOS =================
    addProduct({ id, categoryId, name, description = "", price, promoPrice = 0, image = "", modifierGroups = [], track_stock = false, stock_quantity = 0, auto_paused_by_stock = false }) {
        const newProd = {
            id: id || `prod-${Date.now()}`,
            categoryId,
            name: name.trim(),
            description: description.trim(),
            price: Number(price) || 0,
            promoPrice: Number(promoPrice) || 0,
            image: image || "",
            active: true,
            modifierGroups: modifierGroups || [],
            track_stock: Boolean(track_stock),
            stock_quantity: Math.max(0, parseInt(stock_quantity || 0, 10)),
            auto_paused_by_stock: Boolean(auto_paused_by_stock)
        };
        menuState.update((state) => ({
            ...state,
            products: [newProd, ...state.products]
        }));
        return newProd;
    },

    updateProduct(id, updates) {
        menuState.update((state) => ({
            ...state,
            products: state.products.map((p) => {
                if (p.id !== id) return p;
                const updated = { ...p, ...updates };
                if (updates.track_stock !== undefined) {
                    updated.track_stock = Boolean(updates.track_stock);
                }
                if (updates.stock_quantity !== undefined) {
                    updated.stock_quantity = Math.max(0, parseInt(updates.stock_quantity || 0, 10));
                }
                return updated;
            })
        }));
    },

    deleteProduct(id) {
        menuState.update((state) => ({
            ...state,
            products: state.products.filter((p) => p.id !== id)
        }));
    },

    toggleProductStatus(id) {
        menuState.update((state) => ({
            ...state,
            products: state.products.map((p) =>
                p.id === id ? { ...p, active: !p.active } : p
            )
        }));
    },

    // ================= EXPORTAÇÃO PARA IA DO WHATSAPP =================
    exportMenuForAI() {
        let compiledText = "CARDÁPIO DO RESTAURANTE (ATUALIZADO):\n";
        const state = get(menuState);
        state.categories.forEach((cat) => {
            if (!cat.active) return;
            compiledText += `\n[CATEGORIA: ${cat.icon} ${cat.name}]\n`;
            const prods = state.products.filter((p) => p.categoryId === cat.id && p.active);
            prods.forEach((p) => {
                const finalPrice = p.promoPrice > 0 ? p.promoPrice : p.price;
                compiledText += `- ${p.name} | R$ ${finalPrice.toFixed(2)} (${p.description})\n`;
                if (p.modifierGroups && p.modifierGroups.length > 0) {
                    p.modifierGroups.forEach((mod) => {
                        compiledText += `  * Opções de ${mod.name}: ` + mod.options.map((o) => `${o.name} (+R$ ${o.price.toFixed(2)})`).join(", ") + "\n";
                    });
                }
            });
        });
        return compiledText;
    },

    resetToDefault() {
        menuState.set({
            categories: INITIAL_CATEGORIES,
            products: INITIAL_PRODUCTS
        });
    }
};
